/**
 * Parent Communication Tab Component
 * Handles SMS consent tracking and messaging to parents
 * Includes Twilio compliance features for A2P 10DLC
 */
import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import axios from "axios";
import { useAuth } from "@/contexts/AuthContext";
import { 
  MessageSquare, Phone, Mail, Send, CheckSquare, Square, History, 
  FileCheck, RefreshCw, Printer, CheckCircle, XCircle, Clock, 
  AlertTriangle, Users, Building2, Search, Download, Lock, Loader2,
  DollarSign, Save
} from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { format } from "date-fns";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import FeatureGate from "@/components/FeatureGate";
import { SmsUsageGauge } from "@/components/parent-communication/SmsUsageGauge";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Toggle to false to hide the Email channel from this SMS page (Center Communications hub still supports email blasts)
const ENABLE_EMAIL_CHANNEL = false;

// Format phone number as (XXX) XXX-XXXX
const formatPhoneNumber = (phone) => {
  if (!phone) return "";
  // Remove all non-numeric characters
  const digits = phone.replace(/\D/g, '');
  // Handle 10-digit numbers
  if (digits.length === 10) {
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  }
  // Handle 11-digit numbers starting with 1
  if (digits.length === 11 && digits.startsWith('1')) {
    return `(${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`;
  }
  // Return original if format doesn't match
  return phone;
};

const CATEGORIES = [
  { value: "all", label: "All Categories" },
  { value: "full_time", label: "Full Time" },
  { value: "part_time", label: "Part Time" },
  { value: "school_age", label: "School Age" },
  { value: "after_school", label: "After School" }
];

import { CheckCircle2, CreditCard, Check, AlertTriangle as AlertTriangleIcon } from "lucide-react";

// Parent Communication Square payment links
const PARENT_COMM_SQUARE_LINKS = {
  monthly: "https://square.link/u/GotWKvUK",  // $24.99/month  (NOTE: static link — will be replaced by dynamic checkout in Stage 7)
  annual:  "https://square.link/u/lAst41rP",  // $269.89/year  (NOTE: static link — will be replaced by dynamic checkout in Stage 7)
};

// ── Square Setup Guide — collapsible, hides when setup complete ──
// ── SMS Billing Panel — Send Invoices + Payment Tracker ──
const SmsBillingPanel = ({ smsBilling, childCount }) => {
  const [parents, setParents] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [records, setRecords] = useState([]);
  const [sending, setSending] = useState(false);
  const [billingNote, setBillingNote] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [loadingRecords, setLoadingRecords] = useState(false);
  const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

  useEffect(() => { fetchParents(); fetchRecords(); }, []);

  const fetchParents = async () => {
    try {
      const res = await axios.get(`${API}/children`);
      const kids = (Array.isArray(res.data) ? res.data : []).filter(c => c.active !== false);
      // List each child individually
      const childList = kids.map(c => {
        const p = c.pickupPersons?.[0] || c.parents?.[0] || {};
        const parentName = (p.name || `${p.firstName || ''} ${p.lastName || ''}`.trim()) || 'Unknown Parent';
        const phone = p.phone || p.cellPhone || '';
        const childName = c.name || `${c.firstName || ''} ${c.lastName || ''}`.trim() || 'Unknown Child';
        return { childName, parentName, phone, email: p.email || '', childId: c.id || c._id };
      }).filter(c => c.phone);
      setParents(childList);
    } catch {}
  };

  const fetchRecords = async () => {
    setLoadingRecords(true);
    try {
      const res = await axios.get(`${API}/parent-communication/billing-records`);
      setRecords(res.data?.records || []);
    } catch {} finally { setLoadingRecords(false); }
  };

  const feePerChild = parseFloat(smsBilling.smsPerChildFee) || 0;
  const toggleAll = () => selected.size === parents.length ? setSelected(new Set()) : setSelected(new Set(parents.map((_, i) => i)));

  const handleSend = async () => {
    if (!selected.size) return;
    // Group selected children by parent phone → one invoice per parent
    const byParent = {};
    [...selected].forEach(i => {
      const c = parents[i];
      const key = c.phone;
      if (!byParent[key]) byParent[key] = { parentName: c.parentName, parentPhone: c.phone, parentEmail: c.email, childCount: 0, childNames: [] };
      byParent[key].childCount++;
      byParent[key].childNames.push(c.childName);
    });
    const recipients = Object.values(byParent).map(r => ({
      ...r,
      amountCents: Math.round(r.childCount * feePerChild * 100),
    }));
    setSending(true);
    try {
      const res = await axios.post(`${API}/parent-communication/send-billing-invoices`, { recipients, billingNote, feePassthrough: smsBilling?.feePassthrough !== false });
      toast.success(res.data.message);
      setSelected(new Set()); setBillingNote(''); fetchRecords();
    } catch { toast.error("Failed to send invoices"); } finally { setSending(false); }
  };

  const markPaid = async (id) => {
    try {
      await axios.patch(`${API}/parent-communication/billing-records/${id}/paid`);
      toast.success("Marked as paid"); fetchRecords();
    } catch { toast.error("Failed to update"); }
  };

  const filtered = records.filter(r => statusFilter === 'all' || r.status === statusFilter);
  const totalOwed = records.filter(r => r.status === 'pending').reduce((s, r) => s + (r.amountCents || 0), 0);
  const totalPaid = records.filter(r => r.status === 'paid').reduce((s, r) => s + (r.amountCents || 0), 0);

  return (
    <div className="space-y-4">
      <Card className="border-2 border-purple-300 shadow-lg">
        <CardHeader className="bg-gradient-to-r from-purple-600 to-indigo-700 rounded-t-xl">
          <CardTitle className="flex items-center gap-2 text-white"><Send className="w-5 h-5" />Send Invoices</CardTitle>
          <p className="text-sm text-purple-100">Select parents to bill · ${feePerChild.toFixed(2)}/child/month</p>
        </CardHeader>
        <CardContent className="pt-4 space-y-4">
          <div className="flex items-center justify-between">
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={selected.size === parents.length && parents.length > 0} onChange={toggleAll} className="w-4 h-4 accent-purple-600" />
              <span className="text-sm font-medium text-gray-700">Select All ({parents.length} children)</span>
            </label>
            <span className="text-xs text-gray-500">{selected.size} selected · ${([...selected].reduce((s, i) => s + feePerChild, 0)).toFixed(2)}</span>
          </div>
          <div className="space-y-2 max-h-64 overflow-y-auto">
            {parents.length === 0 ? <p className="text-sm text-gray-400 italic text-center py-4">No children with parent phone numbers found.</p>
              : parents.map((c, i) => (
              <label key={i} className={`flex items-center justify-between gap-3 px-4 py-2.5 rounded-xl border-2 cursor-pointer transition-all ${selected.has(i) ? 'border-purple-500 bg-purple-50' : 'border-gray-200 bg-white hover:border-purple-300'}`}>
                <div className="flex items-center gap-3">
                  <input type="checkbox" checked={selected.has(i)} onChange={() => { const n = new Set(selected); n.has(i) ? n.delete(i) : n.add(i); setSelected(n); }} className="w-4 h-4 accent-purple-600" />
                  <div>
                    <p className="text-sm font-semibold text-gray-800">{c.childName}</p>
                    <p className="text-xs text-gray-400">Parent: {c.parentName} · {c.phone}</p>
                  </div>
                </div>
                <span className="text-sm font-bold text-purple-700">${feePerChild.toFixed(2)}</span>
              </label>
            ))}
          </div>
          <div>
            <label className="text-xs text-gray-500 font-medium">Optional Note to Parents</label>
            <input value={billingNote} onChange={e => setBillingNote(e.target.value)} placeholder="e.g. Due by the 5th of the month"
              className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-400" />
          </div>
          <Button onClick={handleSend} disabled={sending || selected.size === 0}
            className="w-full bg-gradient-to-r from-purple-600 to-indigo-700 hover:from-purple-700 hover:to-indigo-800 text-white py-5 font-semibold rounded-xl shadow">
            {sending ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Sending...</> : <><Send className="w-4 h-4 mr-2" />Send Invoice to {selected.size} Parent{selected.size !== 1 ? 's' : ''}</>}
          </Button>
        </CardContent>
      </Card>

      <Card className="border-2 border-gray-200 shadow-sm">
        <CardHeader className="bg-gradient-to-r from-gray-700 to-gray-800 rounded-t-xl">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <CardTitle className="flex items-center gap-2 text-white"><DollarSign className="w-5 h-5" />Payment Tracker</CardTitle>
            <div className="flex gap-3 text-sm">
              <span className="text-green-300 font-semibold">Paid: ${(totalPaid/100).toFixed(2)}</span>
              <span className="text-amber-300 font-semibold">Pending: ${(totalOwed/100).toFixed(2)}</span>
            </div>
          </div>
        </CardHeader>
        <CardContent className="pt-4 space-y-3">
          <div className="flex gap-2">
            {['all','pending','paid'].map(f => (
              <button key={f} type="button" onClick={() => setStatusFilter(f)}
                className={`px-3 py-1 rounded-lg text-xs font-semibold border capitalize transition-all ${statusFilter === f ? 'bg-gray-800 text-white border-gray-800' : 'bg-white text-gray-600 border-gray-300 hover:border-gray-500'}`}>{f}</button>
            ))}
            <button onClick={fetchRecords} className="ml-auto p-1.5 text-gray-400 hover:text-gray-600 rounded-lg border border-gray-200"><RefreshCw className="w-4 h-4" /></button>
          </div>
          {loadingRecords ? <div className="flex items-center justify-center py-6"><Loader2 className="w-5 h-5 animate-spin text-gray-400" /></div>
          : filtered.length === 0 ? <p className="text-sm text-gray-400 italic text-center py-6">No billing records yet.</p>
          : <div className="overflow-x-auto"><table className="w-full text-xs">
              <thead className="bg-gray-50 border-b border-gray-200"><tr>
                <th className="px-3 py-2 text-left text-gray-600 font-semibold">Parent</th>
                <th className="px-3 py-2 text-left text-gray-600 font-semibold">Amount</th>
                <th className="px-3 py-2 text-left text-gray-600 font-semibold">Sent</th>
                <th className="px-3 py-2 text-left text-gray-600 font-semibold">Status</th>
                <th className="px-3 py-2 text-left text-gray-600 font-semibold">Paid On</th>
                <th className="px-3 py-2"></th>
              </tr></thead>
              <tbody>{filtered.map((r, i) => (
                <tr key={r.id} className={i % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                  <td className="px-3 py-2 font-medium text-gray-800">{r.parentName}</td>
                  <td className="px-3 py-2 font-bold">${((r.amountCents||0)/100).toFixed(2)}</td>
                  <td className="px-3 py-2 text-gray-500">{r.sentAt ? new Date(r.sentAt).toLocaleDateString() : '—'}</td>
                  <td className="px-3 py-2">{r.status === 'paid'
                    ? <span className="flex items-center gap-1 text-green-600 font-semibold"><CheckCircle2 className="w-3.5 h-3.5" />Paid</span>
                    : <span className="flex items-center gap-1 text-amber-600"><Clock className="w-3.5 h-3.5" />Pending</span>}
                  </td>
                  <td className="px-3 py-2 text-gray-500">{r.paidAt ? new Date(r.paidAt).toLocaleDateString() : '—'}</td>
                  <td className="px-3 py-2">{r.status === 'pending' && <button onClick={() => markPaid(r.id)} className="text-xs bg-green-600 hover:bg-green-700 text-white px-2 py-1 rounded-lg">Mark Paid</button>}</td>
                </tr>
              ))}</tbody>
            </table></div>}
        </CardContent>
      </Card>
    </div>
  );
};


const SquareSetupGuide = ({ squareConnected, onConnect }) => {
  const [expanded, setExpanded] = useState(!squareConnected);

  const steps = [
    {
      num: 1, done: squareConnected,
      title: "Connect Your Square Account",
      detail: "Link your Square account to this app. The app will automatically generate payment links and SMS them to parents on your billing date — no catalog items or subscription plans needed.",
      action: !squareConnected ? { label: "Connect Square Account →", onClick: onConnect } : null
    },
    {
      num: 2, done: true,
      title: "Add Webhook in Square Developer Console",
      detail: (
        <span>Go to <strong>Square Developer Console → Webhooks</strong> → add this URL: <strong className="text-blue-700 break-all">https://login.todayskidzsimplesignin.com/api/square/webhook</strong> — subscribe to the <em>payment.completed</em> event. This tells the app when a parent pays.</span>
      )
    },
    {
      num: 3, done: true,
      title: "You're done — the app handles the rest",
      detail: "Set your fee per child and billing date below. On billing day, the app automatically calculates each parent's charge, generates a Square payment link, and SMS it to them. When they pay, you'll see it in the payment tracker."
    },
  ];

  if (!expanded && squareConnected) {
    return (
      <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <CheckCircle2 className="w-4 h-4 text-emerald-600 flex-shrink-0" />
          <span className="text-sm text-emerald-700 font-medium">Square setup complete — account connected</span>
        </div>
        <button onClick={() => setExpanded(true)} className="text-xs text-gray-400 hover:text-gray-600 underline ml-3 flex-shrink-0">View guide</button>
      </div>
    );
  }

  return (
    <div className="rounded-lg border border-blue-200 bg-blue-50/50 p-3 space-y-3">
      <div className="flex items-center justify-between">
        <p className="text-sm font-semibold text-blue-800">Square Setup Guide</p>
        {squareConnected && (
          <button onClick={() => setExpanded(false)} className="text-xs text-gray-500 hover:text-gray-700 underline">Hide — setup complete</button>
        )}
      </div>
      <div className="space-y-3">
        {steps.map(step => (
          <div key={step.num} className={`flex gap-3 p-3 rounded-lg border ${step.done ? 'bg-white border-gray-200' : 'bg-rose-50 border-rose-200'}`}>
            <div className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 text-xs font-bold mt-0.5 ${step.done ? 'bg-emerald-500 text-white' : 'bg-rose-400 text-white'}`}>
              {step.done ? '✓' : step.num}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-gray-800">{step.title}</p>
              <p className="text-xs text-gray-500 mt-0.5">{step.detail}</p>
              {step.action && (
                <button onClick={step.action.onClick}
                  className="mt-2 text-xs bg-blue-600 hover:bg-blue-700 text-white px-3 py-1.5 rounded-lg font-medium">
                  {step.action.label}
                </button>
              )}
            </div>
          </div>
        ))}
      </div>
      <p className="text-xs text-slate-400">Fees: 2.9% + $0.30 (Square) + Our $5 platform fee can be passed to parent</p>
    </div>
  );
};

export const ParentCommunicationTab = ({ children, hideBilling = false }) => {
  const { organization, user } = useAuth();
  const navigate = useNavigate();
  const isOwner = user?.role === 'owner' || user?.role === 'co-owner' || user?.isPrimaryOwner;
  const isPlatformOwner = organization?.isOwnerAccount === true;
  const [activeSubTab, setActiveSubTab] = useState("messaging");

  // Subscription state
  const [selectedPlan, setSelectedPlan] = useState('monthly');
  const [subscribing, setSubscribing] = useState(false);
  const [canceling, setCanceling] = useState(false);

  // Parent Billing state
  const [smsBilling, setSmsBilling] = useState({
    chargeParents: false,
    smsPerChildFee: 3.00,
    billingFrequency: 'monthly',
    billingDay: 1,
    billingMode: 'auto',
    feePassthrough: true,
    estimateChildren: '',
    zelleEmail: '',
    zellePhone: '',
    parentSquareEnabled: false,
    squareConnected: false,
  });
  const [savingBilling, setSavingBilling] = useState(false);
  const [childCount, setChildCount] = useState(0);
  
  // SMS Subscription State
  const [smsSubscribed, setSmsSubscribed] = useState(false);
  const [loadingSubscription, setLoadingSubscription] = useState(true);
  
  // SMS Consent State
  const [consents, setConsents] = useState([]);
  const [loadingConsents, setLoadingConsents] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [searchTerm, setSearchTerm] = useState(""); // Search filter for consents
  
  // Confirmation Dialog State
  const [showOptInConfirm, setShowOptInConfirm] = useState(false);
  const [showMessageConfirm, setShowMessageConfirm] = useState(false);
  
  // Editable Opt-In Message
  const defaultOptInMessage = `Reply YES to consent to receive text messages about your child's care at our center. Msg & data rates may apply. Reply STOP to opt out at any time.`;
  const [optInMessage, setOptInMessage] = useState(defaultOptInMessage);
  const [editingMessage, setEditingMessage] = useState(false);
  
  // Messaging State
  const [parentContacts, setParentContacts] = useState([]);
  const [staffContacts, setStaffContacts] = useState([]);
  const [audience, setAudience] = useState("parents"); // "parents" | "staff" | "both"
  const [selectedParents, setSelectedParents] = useState([]);
  const [parentFilterCategory, setParentFilterCategory] = useState("all");
  const [messageType, setMessageType] = useState("sms");
  const [messageSubject, setMessageSubject] = useState("");
  const [messageBody, setMessageBody] = useState("");
  const [senderName, setSenderName] = useState("");
  const [includeConsent, setIncludeConsent] = useState(true);
  const [sendingMessage, setSendingMessage] = useState(false);
  const [messageHistory, setMessageHistory] = useState([]);
  const [showMessageHistory, setShowMessageHistory] = useState(false);

  useEffect(() => {
    fetchSmsSubscription();
    fetchSmsBilling();
    fetchConsents();
    fetchParentContacts();
    fetchStaffContacts();
    fetchMessageHistory();
  }, []);

  // Fetch Parent Communication subscription status (separate from Pickup Safety $14.99)
  const fetchSmsSubscription = async () => {
    try {
      const res = await axios.get(`${API}/parent-communication/settings`);
      setSmsSubscribed(res.data?.settings?.paymentOptions?.centerSubscriptionActive || false);
    } catch (error) {
      console.error("Error fetching Parent Communication subscription:", error);
      setSmsSubscribed(false);
    } finally {
      setLoadingSubscription(false);
    }
  };

  // ============== SUBSCRIPTION HANDLERS ==============

  const handleSubscribe = async () => {
    setSubscribing(true);
    try {
      // Redirect to Square subscription checkout page
      const squareUrl = selectedPlan === 'annual'
        ? PARENT_COMM_SQUARE_LINKS.annual
        : PARENT_COMM_SQUARE_LINKS.monthly;
      window.open(squareUrl, '_blank');
    } catch {
      toast.error("Failed to initiate subscription");
    } finally {
      setSubscribing(false);
    }
  };

  const handleCancelSubscription = async () => {
    setCanceling(true);
    try {
      await axios.post(`${API}/parent-communication/cancel-subscription`);
      setSmsSubscribed(false);
      toast.success("Subscription cancelled");
    } catch {
      toast.error("Failed to cancel subscription");
    } finally {
      setCanceling(false);
    }
  };

  // ============== SMS BILLING FUNCTIONS ==============

  const fetchSmsBilling = async () => {
    try {
      const [settingsRes, childrenRes] = await Promise.all([
        axios.get(`${API}/parent-communication/settings`),
        axios.get(`${API}/children`)
      ]);
      const s = settingsRes.data?.settings || {};
      const pb = s.parentBillingSettings || {};
      setSmsBilling(prev => ({
        ...prev,
        chargeParents: pb.chargeParents || false,
        smsPerChildFee: pb.smsPerChildFee ?? pb.feePerChild ?? 3.00,
        billingFrequency: pb.billingFrequency || 'monthly',
        zelleEmail: s.paymentOptions?.parentZelleEmail || '',
        zellePhone: s.paymentOptions?.parentZellePhone || '',
        parentSquareEnabled: s.paymentOptions?.parentSquareEnabled || false,
        squareConnected: !!settingsRes.data?.squareConnected,
      }));
      setChildCount((childrenRes.data || []).filter(c => c.active !== false).length);
    } catch {}
  };

  const handleBillingChange = (field, value) => {
    setSmsBilling(prev => ({ ...prev, [field]: value }));
  };

  const saveSmsBilling = async () => {
    setSavingBilling(true);
    try {
      await axios.post(`${API}/parent-communication/save-settings`, {
        parentBillingSettings: {
          chargeParents: smsBilling.chargeParents,
          smsPerChildFee: smsBilling.smsPerChildFee,
          billingFrequency: smsBilling.billingFrequency,
        },
        paymentOptions: {
          parentZelleEmail: smsBilling.zelleEmail,
          parentZellePhone: smsBilling.zellePhone,
          parentSquareEnabled: smsBilling.parentSquareEnabled,
        }
      });
      toast.success("Billing settings saved");
    } catch { toast.error("Failed to save billing settings"); }
    finally { setSavingBilling(false); }
  };

  // ============== SMS CONSENT FUNCTIONS ==============

  const fetchConsents = async () => {
    setLoadingConsents(true);
    try {
      const res = await axios.get(`${API}/sms-consent`);
      const allConsents = res.data?.consents || [];
      
      // Deduplicate by phone number - keep the first occurrence and merge child names
      const phoneMap = new Map();
      allConsents.forEach(consent => {
        // Normalize phone to compare
        const normalizedPhone = consent.phoneNumber?.replace(/\D/g, '') || '';
        if (phoneMap.has(normalizedPhone)) {
          // Merge child names if different
          const existing = phoneMap.get(normalizedPhone);
          if (!existing.childName.includes(consent.childName)) {
            existing.childName = `${existing.childName}, ${consent.childName}`;
          }
          // Keep the most "complete" record (one with opt-in or form signed)
          if (consent.smsOptInReceived && !existing.smsOptInReceived) {
            existing.smsOptInReceived = consent.smsOptInReceived;
            existing.smsOptInReceivedAt = consent.smsOptInReceivedAt;
          }
          if (consent.enrollmentFormSigned && !existing.enrollmentFormSigned) {
            existing.enrollmentFormSigned = consent.enrollmentFormSigned;
            existing.enrollmentFormSignedDate = consent.enrollmentFormSignedDate;
          }
          if (consent.optInRequestSent && !existing.optInRequestSent) {
            existing.optInRequestSent = consent.optInRequestSent;
            existing.optInRequestSentAt = consent.optInRequestSentAt;
          }
        } else {
          phoneMap.set(normalizedPhone, { ...consent });
        }
      });
      
      setConsents(Array.from(phoneMap.values()));
    } catch (error) {
      console.error("Error fetching consents:", error);
    }
    setLoadingConsents(false);
  };

  const syncFromChildren = async () => {
    setSyncing(true);
    try {
      const res = await axios.get(`${API}/sms-consent/sync-from-children`);
      toast.success(res.data.message);
      await fetchConsents();
    } catch (error) {
      console.error("Error syncing:", error);
      toast.error("Failed to sync contacts");
    }
    setSyncing(false);
  };

  const updateConsent = async (consentId, field, value) => {
    try {
      const updateData = { [field]: value };
      // When checking the form signed box, set the date to now
      if (field === 'enrollmentFormSigned' && value === true) {
        updateData.enrollmentFormSignedDate = new Date().toISOString();
      }
      // When unchecking the form signed box, clear the date
      if (field === 'enrollmentFormSigned' && value === false) {
        updateData.enrollmentFormSignedDate = null;
      }
      
      await axios.put(`${API}/sms-consent/${consentId}`, updateData);
      await fetchConsents();
      toast.success("Consent updated");
    } catch (error) {
      console.error("Error updating consent:", error);
      toast.error("Failed to update consent");
    }
  };

  const recordOptIn = async (consentId) => {
    try {
      await axios.post(`${API}/sms-consent/${consentId}/record-optin`);
      await fetchConsents();
      toast.success("Opt-in recorded with timestamp");
    } catch (error) {
      console.error("Error recording opt-in:", error);
      toast.error("Failed to record opt-in");
    }
  };

  const resetOptIn = async (consentId) => {
    try {
      await axios.post(`${API}/sms-consent/${consentId}/reset-optin`);
      await fetchConsents();
      toast.success("Opt-in reset to pending");
    } catch (error) {
      console.error("Error resetting opt-in:", error);
      toast.error("Failed to reset opt-in");
    }
  };

  // Send opt-in request SMS to selected parents
  const [selectedConsents, setSelectedConsents] = useState([]);
  const [sendingOptIn, setSendingOptIn] = useState(false);

  const toggleConsentSelection = (consentId) => {
    setSelectedConsents(prev => 
      prev.includes(consentId) 
        ? prev.filter(id => id !== consentId)
        : [...prev, consentId]
    );
  };

  const selectAllPendingConsents = () => {
    const pending = consents.filter(c => !c.smsOptInReceived).map(c => c.id);
    setSelectedConsents(pending);
  };

  const clearConsentSelection = () => {
    setSelectedConsents([]);
  };

  // Show confirmation before sending opt-in requests
  const confirmSendOptInRequests = () => {
    if (selectedConsents.length === 0) {
      toast.error("Please select at least one parent");
      return;
    }
    setShowOptInConfirm(true);
  };

  const sendOptInRequests = async () => {
    setShowOptInConfirm(false);
    
    if (selectedConsents.length === 0) {
      toast.error("Please select at least one parent");
      return;
    }

    setSendingOptIn(true);
    try {
      const res = await axios.post(`${API}/sms-consent/send-optin-request`, {
        consentIds: selectedConsents,
        customMessage: optInMessage
      });
      
      if (res.data.sent_count > 0) {
        toast.success(`Opt-in requests sent to ${res.data.sent_count} parents`);
      }
      
      if (res.data.failed_count > 0) {
        const firstErrors = (res.data.failed || []).slice(0, 3)
          .map(f => `${f.phone || 'unknown'}: ${f.error || 'unknown error'}`)
          .join('\n');
        toast.error(
          `Failed to send to ${res.data.failed_count} parents.\n${firstErrors}`,
          { duration: 12000 }
        );
        console.warn("Failed:", res.data.failed);
      }
      
      setSelectedConsents([]);
      await fetchConsents();
    } catch (error) {
      console.error("Error sending opt-in requests:", error);
      const errMsg = error.response?.data?.detail || "Failed to send opt-in requests";
      toast.error(errMsg);
    }
    setSendingOptIn(false);
  };

  const getConsentStats = () => {
    const total = consents.length;
    const enrolled = consents.filter(c => c.enrollmentFormSigned).length;
    const optedIn = consents.filter(c => c.smsOptInReceived).length;
    const fullyConsented = consents.filter(c => c.enrollmentFormSigned && c.smsOptInReceived).length;
    const requestsSent = consents.filter(c => c.optInRequestSent).length;
    const pending = consents.filter(c => !c.smsOptInReceived).length;
    return { total, enrolled, optedIn, fullyConsented, requestsSent, pending };
  };

  // Filter consents by search term (name or phone)
  const filteredConsents = consents.filter(consent => {
    if (!searchTerm.trim()) return true;
    const term = searchTerm.toLowerCase().trim();
    const nameMatch = consent.parentName?.toLowerCase().includes(term);
    const childMatch = consent.childName?.toLowerCase().includes(term);
    // Search phone with or without formatting
    const phoneDigits = consent.phoneNumber?.replace(/\D/g, '') || '';
    const searchDigits = term.replace(/\D/g, '');
    const phoneMatch = searchDigits 
      ? phoneDigits.includes(searchDigits) 
      : consent.phoneNumber?.toLowerCase().includes(term);
    return nameMatch || childMatch || phoneMatch;
  });

  // ============== PDF EXPORT FOR AUDIT ==============

  // Generate Compliance Status Report (for licensing inspections)
  const generateComplianceStatusPDF = () => {
    const doc = new jsPDF();
    const orgName = organization?.name || "Childcare Center";
    const today = format(new Date(), "MMMM d, yyyy");
    const stats = getConsentStats();

    // Header bar
    doc.setFillColor(79, 70, 229);
    doc.rect(0, 0, 220, 28, 'F');
    doc.setFontSize(16);
    doc.setTextColor(255, 255, 255);
    doc.setFont("helvetica", "bold");
    doc.text(orgName, 14, 12);
    doc.setFontSize(11);
    doc.setFont("helvetica", "normal");
    doc.text("Parent SMS Consent — Compliance Status Report", 14, 21);

    // Generated date + purpose
    doc.setFontSize(9);
    doc.setTextColor(100, 100, 100);
    doc.text(`Generated: ${today}  |  For Licensing Inspection Use`, 14, 34);

    // Summary box
    doc.setDrawColor(220, 220, 220);
    doc.setFillColor(248, 250, 252);
    doc.roundedRect(14, 38, 182, 22, 2, 2, 'FD');
    doc.setFontSize(9);
    doc.setTextColor(60, 60, 60);
    doc.setFont("helvetica", "bold");
    doc.text("Summary", 18, 45);
    doc.setFont("helvetica", "normal");
    doc.text(`Total Parents: ${stats.total}`, 18, 52);
    doc.text(`Forms Signed: ${stats.enrolled}`, 58, 52);
    doc.text(`SMS Opt-In: ${stats.optedIn}`, 100, 52);
    doc.text(`Fully Compliant: ${stats.fullyConsented}`, 140, 52);

    // Table
    const tableData = consents.map((c, idx) => {
      let status = "Non-Compliant";
      if (c.enrollmentFormSigned && c.smsOptInReceived) status = "Compliant";
      else if (c.enrollmentFormSigned || c.smsOptInReceived) status = "Partial";
      return [
        idx + 1,
        c.parentName,
        c.childName,
        formatPhoneNumber(c.phoneNumber),
        c.enrollmentFormSigned ? "YES" : "NO",
        c.enrollmentFormSignedDate ? format(new Date(c.enrollmentFormSignedDate), "M/d/yy") : "—",
        c.smsOptInReceived ? "YES" : "NO",
        c.smsOptInReceivedAt ? format(new Date(c.smsOptInReceivedAt), "M/d/yy") : "—",
        status,
      ];
    });

    autoTable(doc, {
      startY: 64,
      head: [["#", "Parent Name", "Child", "Phone", "Form Signed", "Sign Date", "SMS Opt-In", "Opt-In Date", "Status"]],
      body: tableData,
      headStyles: { fillColor: [79, 70, 229], textColor: [255, 255, 255], fontStyle: "bold", fontSize: 8 },
      styles: { fontSize: 7.5, cellPadding: 2 },
      columnStyles: {
        0: { halign: "center", cellWidth: 7 },
        1: { cellWidth: 32 },
        2: { cellWidth: 28 },
        3: { cellWidth: 25 },
        4: { halign: "center", cellWidth: 16 },
        5: { halign: "center", cellWidth: 17 },
        6: { halign: "center", cellWidth: 16 },
        7: { halign: "center", cellWidth: 18 },
        8: { halign: "center", cellWidth: 20 },
      },
      alternateRowStyles: { fillColor: [245, 243, 255] },
      didParseCell: (data) => {
        if (data.column.index === 8 && data.section === 'body') {
          const val = data.cell.raw;
          if (val === "Compliant") data.cell.styles.textColor = [22, 163, 74];
          else if (val === "Partial") data.cell.styles.textColor = [234, 88, 12];
          else data.cell.styles.textColor = [220, 38, 38];
          data.cell.styles.fontStyle = "bold";
        }
      }
    });

    // Footer
    const pageCount = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFontSize(8);
      doc.setTextColor(140, 140, 140);
      doc.text(
        `${orgName} — SMS Consent Compliance Report — Page ${i} of ${pageCount}`,
        doc.internal.pageSize.getWidth() / 2,
        doc.internal.pageSize.getHeight() - 10,
        { align: "center" }
      );
    }

    doc.autoPrint();
    const pdfUrl = URL.createObjectURL(doc.output('blob'));
    window.open(pdfUrl, '_blank');
    toast.success("Compliance report ready to print");
  };

  // Print audit report with print dialog
  const printAuditReport = () => {
    const doc = new jsPDF();
    const orgName = organization?.name || "Childcare Center";
    const stats = getConsentStats();
    
    // Header
    doc.setFontSize(18);
    doc.setTextColor(100, 50, 150);
    doc.text(orgName, 14, 20);
    
    doc.setFontSize(14);
    doc.setTextColor(0, 0, 0);
    doc.text("SMS Consent Authorization Records", 14, 30);
    
    // Compliance Statement
    doc.setFontSize(10);
    doc.setTextColor(80, 80, 80);
    const statement = `This document certifies that ${orgName} maintains written consent records for SMS text message communications with parents/guardians. All listed individuals have provided explicit opt-in consent for receiving text messages regarding their enrolled children's care and activities. This consent was obtained through enrollment form signatures and/or direct SMS opt-in confirmations.`;
    
    const splitStatement = doc.splitTextToSize(statement, 180);
    doc.text(splitStatement, 14, 42);
    
    // Stats Summary
    doc.setFontSize(11);
    doc.setTextColor(0, 0, 0);
    doc.text(`Generated: ${format(new Date(), "MMMM d, yyyy 'at' h:mm a")}`, 14, 65);
    doc.text(`Total Contacts: ${stats.total}`, 14, 72);
    doc.text(`Enrollment Forms Signed: ${stats.enrolled}`, 14, 79);
    doc.text(`SMS Opt-In Received: ${stats.optedIn}`, 14, 86);
    doc.text(`Fully Consented: ${stats.fullyConsented}`, 14, 93);
    
    // Table
    const tableData = consents.map((c, idx) => [
      idx + 1,
      c.parentName,
      formatPhoneNumber(c.phoneNumber),
      c.childName,
      c.enrollmentFormSigned ? "YES" : "NO",
      c.enrollmentFormSignedDate ? format(new Date(c.enrollmentFormSignedDate), "M/d/yyyy") : "-",
      c.smsOptInReceived ? "YES" : "NO",
      c.smsOptInReceivedAt ? format(new Date(c.smsOptInReceivedAt), "M/d/yyyy h:mm a") : "-"
    ]);

    autoTable(doc, {
      startY: 100,
      head: [["#", "Parent Name", "Phone Number", "Child Name", "Form Signed", "Sign Date", "SMS Opt-In", "Opt-In Date/Time"]],
      body: tableData,
      headStyles: {
        fillColor: [100, 50, 150],
        textColor: [255, 255, 255],
        fontStyle: "bold",
        fontSize: 8
      },
      styles: {
        fontSize: 7,
        cellPadding: 2
      },
      columnStyles: {
        0: { halign: "center", cellWidth: 8 },
        1: { cellWidth: 30 },
        2: { cellWidth: 25 },
        3: { cellWidth: 30 },
        4: { halign: "center", cellWidth: 15 },
        5: { halign: "center", cellWidth: 20 },
        6: { halign: "center", cellWidth: 15 },
        7: { cellWidth: 30 }
      },
      alternateRowStyles: {
        fillColor: [245, 240, 255]
      }
    });

    // Footer
    const pageCount = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFontSize(8);
      doc.setTextColor(100, 100, 100);
      doc.text(
        `${orgName} - SMS Consent Records - Page ${i} of ${pageCount}`,
        doc.internal.pageSize.getWidth() / 2,
        doc.internal.pageSize.getHeight() - 10,
        { align: "center" }
      );
    }

    // Open print dialog instead of saving
    doc.autoPrint();
    const pdfBlob = doc.output('blob');
    const pdfUrl = URL.createObjectURL(pdfBlob);
    window.open(pdfUrl, '_blank');
    toast.success("Print dialog opened");
  };

  // Download PDF directly using authenticated request
  const downloadPDF = async () => {
    try {
      toast.info("Generating PDF...");
      const response = await axios.get(`${API}/sms-consent/export-pdf`, {
        responseType: 'blob',
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `sms_consent_records_${format(new Date(), "yyyy-MM-dd")}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success("PDF downloaded successfully");
    } catch (error) {
      console.error("Error downloading PDF:", error);
      toast.error("Failed to download PDF. Please try again.");
    }
  };

  // ============== MESSAGING FUNCTIONS ==============

  const fetchParentContacts = async () => {
    try {
      const res = await axios.get(`${API}/parents/contacts`);
      setParentContacts(res.data?.contacts || []);
    } catch (error) {
      console.error("Error fetching contacts:", error);
    }
  };

  const fetchStaffContacts = async () => {
    try {
      const res = await axios.get(`${API}/staff/contacts`);
      setStaffContacts(res.data?.contacts || []);
    } catch (error) {
      console.error("Error fetching staff contacts:", error);
    }
  };

  const fetchMessageHistory = async () => {
    try {
      const res = await axios.get(`${API}/parents/messages`);
      setMessageHistory(res.data?.messages || []);
    } catch (error) {
      console.error("Error fetching message history:", error);
    }
  };

  // Dedupe contacts by phone (SMS) or email (Email). When a parent and a staff
  // member share the same phone/email, the row is marked as STAFF and shows
  // both names so the director can clearly see the person.
  const filteredContacts = useMemo(() => {
    // Combine parents + staff based on audience selector — STAFF FIRST so a
    // shared phone is identified as staff (parent context merges in afterwards).
    const useParents = audience === 'parents' || audience === 'both';
    const useStaff = audience === 'staff' || audience === 'both';
    const combined = [
      ...(useStaff ? staffContacts : []),
      ...(useParents ? parentContacts : []),
    ];
    const filtered = combined
      .filter(c => c.contactType === 'staff' || parentFilterCategory === 'all' || c.childCategory === parentFilterCategory)
      .filter(c => messageType === 'sms' ? c.phone : c.email);

    const map = new Map();
    filtered.forEach(c => {
      const key = messageType === 'sms'
        ? (c.phone || '').replace(/\D/g, '')
        : (c.email || '').toLowerCase().trim();
      if (!key) return;
      if (map.has(key)) {
        const existing = map.get(key);
        if (c.childName && !existing.childNames.includes(c.childName)) {
          existing.childNames.push(c.childName);
          existing.childIds.push(c.childId);
        }
        if (c.contactType === 'staff') {
          existing.contactType = 'staff';
        }
        if (c.parentName && !existing.parentName.split(' / ').includes(c.parentName)) {
          existing.parentName = `${existing.parentName} / ${c.parentName}`;
        }
      } else {
        map.set(key, {
          ...c,
          dedupKey: key,
          childNames: c.childName ? [c.childName] : [],
          childIds: c.childId ? [c.childId] : [],
        });
      }
    });
    return Array.from(map.values());
  }, [parentContacts, staffContacts, parentFilterCategory, messageType, audience]);

  const handleSelectAll = () => setSelectedParents(filteredContacts);
  const handleClearSelection = () => setSelectedParents([]);

  const toggleContact = (contact) => {
    const isSelected = selectedParents.some(p => p.dedupKey === contact.dedupKey);
    if (isSelected) {
      setSelectedParents(selectedParents.filter(p => p.dedupKey !== contact.dedupKey));
    } else {
      setSelectedParents([...selectedParents, contact]);
    }
  };

  const handleSendMessage = async () => {
    setShowMessageConfirm(false);
    
    if (selectedParents.length === 0) {
      toast.error("Please select at least one recipient");
      return;
    }
    
    if (!messageBody.trim()) {
      toast.error("Please enter a message");
      return;
    }
    
    if (messageType === 'email' && !messageSubject.trim()) {
      toast.error("Please enter a subject for the email");
      return;
    }

    if (messageType === 'sms' && !senderName.trim()) {
      toast.error("Your name is required so parents know who is texting them");
      return;
    }

    setSendingMessage(true);
    try {
      const response = await axios.post(`${API}/parents/send-message`, {
        messageType,
        subject: messageSubject,
        message: messageBody,
        recipients: selectedParents,
        includeConsent,
        senderName: senderName.trim()
      });

      if (response.data.sent_count > 0) {
        toast.success(`Message sent to ${response.data.sent_count} recipients`);
        setMessageBody("");
        setMessageSubject("");
        setSenderName("");
        setSelectedParents([]);
        await fetchMessageHistory();
      } else {
        const failedList = response.data.failed || [];
        const firstErrors = failedList.slice(0, 3).join('\n');
        toast.error(
          `No messages were sent.\n${firstErrors || 'No recipient details returned.'}`,
          { duration: 12000 }
        );
        console.warn("Failed:", failedList);
      }
    } catch (error) {
      console.error("Error sending message:", error);
      toast.error(error.response?.data?.detail || "Failed to send message");
    } finally {
      setSendingMessage(false);
    }
  };

  // Show confirmation before sending message
  const confirmSendMessage = () => {
    if (selectedParents.length === 0) {
      toast.error("Please select at least one recipient");
      return;
    }
    
    if (!messageBody.trim()) {
      toast.error("Please enter a message");
      return;
    }
    
    if (messageType === 'email' && !messageSubject.trim()) {
      toast.error("Please enter a subject for the email");
      return;
    }
    
    if (messageType === 'sms' && !senderName.trim()) {
      toast.error("Your name is required so parents know who is texting them");
      return;
    }
  
    setShowMessageConfirm(true);
  };

  const stats = getConsentStats();

  return (
    <FeatureGate feature="parent_communication">
    <Card className="bg-white/90 backdrop-blur-sm border-2 border-purple-200">
      <CardHeader>
        <div className="flex flex-col gap-2">
          {/* Organization Name */}
          <div className="flex items-center gap-2 text-purple-600">
            <Building2 className="w-5 h-5" />
            <span className="font-semibold">{organization?.name || "Childcare Center"}</span>
          </div>
          
          <CardTitle className="text-2xl text-purple-800 flex items-center gap-3">
            <MessageSquare className="w-7 h-7" />
            Parent Communication
          </CardTitle>
          
          {/* Compliance Statement */}
          <div className="bg-purple-50 border border-purple-200 rounded-lg p-3 text-sm text-purple-800">
            <p className="font-medium flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 text-amber-600" />
              SMS Consent Compliance Notice
            </p>
            <p className="text-xs text-purple-600 mt-1">
              This system tracks SMS opt-in consent for Twilio A2P 10DLC compliance. All parents must provide 
              written consent (enrollment form) and/or SMS opt-in confirmation before receiving text messages. 
              Maintain these records for potential carrier audits.
            </p>
          </div>
        </div>
      </CardHeader>
      
      <CardContent>
        <div className="relative">
          {/* Content — faded only for non-owner non-subscribers */}
          <div className={(!smsSubscribed) ? "opacity-40 pointer-events-none select-none" : ""}>
        {/* Live SMS Usage Gauge — visible to subscribers and platform owners */}
        {(smsSubscribed || isPlatformOwner) && (
          <div className="mb-4">
            <SmsUsageGauge />
          </div>
        )}
        <Tabs value={activeSubTab} onValueChange={setActiveSubTab}>
          <TabsList className="grid grid-cols-2 gap-2 mb-6">
            <TabsTrigger 
              value="messaging" 
              className="data-[state=active]:bg-purple-500 data-[state=active]:text-white flex items-center gap-2"
            >
              <Send className="w-4 h-4" />
              Send Messages
            </TabsTrigger>
            <TabsTrigger 
              value="consent" 
              className="data-[state=active]:bg-purple-500 data-[state=active]:text-white flex items-center gap-2"
            >
              <FileCheck className="w-4 h-4" />
              SMS Consent Management
            </TabsTrigger>
          </TabsList>

          {/* ============== SMS CONSENT TAB ============== */}
          <TabsContent value="consent" className="space-y-6">
            {/* Stats Cards */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              <Card className="bg-blue-50 border-blue-200">
                <CardContent className="pt-4 text-center">
                  <Users className="w-8 h-8 text-blue-500 mx-auto mb-2" />
                  <p className="text-2xl font-bold text-blue-700">{stats.total}</p>
                  <p className="text-xs text-blue-600">Total Contacts</p>
                </CardContent>
              </Card>
              <Card className="bg-amber-50 border-amber-200">
                <CardContent className="pt-4 text-center">
                  <FileCheck className="w-8 h-8 text-amber-500 mx-auto mb-2" />
                  <p className="text-2xl font-bold text-amber-700">{stats.enrolled}</p>
                  <p className="text-xs text-amber-600">Forms Signed</p>
                </CardContent>
              </Card>
              <Card className="bg-cyan-50 border-cyan-200">
                <CardContent className="pt-4 text-center">
                  <Send className="w-8 h-8 text-cyan-500 mx-auto mb-2" />
                  <p className="text-2xl font-bold text-cyan-700">{stats.requestsSent}</p>
                  <p className="text-xs text-cyan-600">Requests Sent</p>
                </CardContent>
              </Card>
              <Card className="bg-green-50 border-green-200">
                <CardContent className="pt-4 text-center">
                  <Phone className="w-8 h-8 text-green-500 mx-auto mb-2" />
                  <p className="text-2xl font-bold text-green-700">{stats.optedIn}</p>
                  <p className="text-xs text-green-600">SMS Opt-Ins</p>
                </CardContent>
              </Card>
              <Card className="bg-purple-50 border-purple-200">
                <CardContent className="pt-4 text-center">
                  <CheckCircle className="w-8 h-8 text-purple-500 mx-auto mb-2" />
                  <p className="text-2xl font-bold text-purple-700">{stats.fullyConsented}</p>
                  <p className="text-xs text-purple-600">Fully Consented</p>
                </CardContent>
              </Card>
            </div>

            {/* Opt-In Message Preview/Edit */}
            <Card className="bg-blue-50 border-blue-200">
              <CardContent className="pt-4">
                <div className="flex items-center justify-between mb-2">
                  <p className="font-medium text-blue-800 flex items-center gap-2">
                    <MessageSquare className="w-4 h-4" />
                    Opt-In Request Message (sent to parents)
                  </p>
                  <Button
                    size="sm"
                    variant="outline"
                    className="text-xs border-blue-300 text-blue-700"
                    onClick={() => setEditingMessage(!editingMessage)}
                  >
                    {editingMessage ? "Done Editing" : "Edit Message"}
                  </Button>
                </div>
                
                {editingMessage ? (
                  <div className="space-y-2">
                    <div className="bg-white rounded-lg p-2 text-sm border border-blue-300">
                      <p className="font-medium text-gray-700 mb-1">{organization?.name || "Childcare Center"}:</p>
                      <Textarea
                        value={optInMessage}
                        onChange={(e) => setOptInMessage(e.target.value)}
                        className="min-h-[80px] border-blue-200 text-sm"
                        placeholder="Enter your opt-in message..."
                      />
                    </div>
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        className="text-xs border-gray-300"
                        onClick={() => setOptInMessage(defaultOptInMessage)}
                      >
                        Reset to Default
                      </Button>
                    </div>
                    <p className="text-xs text-amber-600">
                      ⚠️ Message must include: opt-in instruction (Reply YES), data rates notice, and opt-out option (Reply STOP) for compliance.
                    </p>
                  </div>
                ) : (
                  <div className="bg-white rounded-lg p-3 text-sm text-gray-700 border border-blue-200">
                    <p className="font-medium">{organization?.name || "Childcare Center"}:</p>
                    <p>{optInMessage}</p>
                  </div>
                )}
                
                <p className="text-xs text-blue-600 mt-2">
                  When parents reply "YES", the system automatically records their opt-in with a timestamp.
                </p>
              </CardContent>
            </Card>

            {/* Action Buttons */}
            <div className="flex flex-wrap gap-2">
              <Button 
                onClick={syncFromChildren}
                disabled={syncing}
                variant="outline" 
                className="border-blue-300 text-blue-700"
              >
                <RefreshCw className={`w-4 h-4 mr-2 ${syncing ? 'animate-spin' : ''}`} />
                {syncing ? "Syncing..." : "Sync from Children & Staff"}
              </Button>
              <Button
                onClick={generateComplianceStatusPDF}
                variant="outline"
                className="border-indigo-300 text-indigo-700 hover:bg-indigo-50"
                data-testid="compliance-report-btn"
              >
                <Printer className="w-4 h-4 mr-2" />
                Compliance Status Report
              </Button>
              <Button 
                onClick={printAuditReport}
                variant="outline" 
                className="border-red-300 text-red-700"
              >
                <Printer className="w-4 h-4 mr-2" />
                Print Audit Report
              </Button>
              <Button 
                onClick={downloadPDF}
                variant="outline" 
                className="border-green-300 text-green-700"
              >
                <Download className="w-4 h-4 mr-2" />
                Download PDF
              </Button>
            </div>

            {/* Consent Records Table */}
            <div className="border-2 border-purple-100 rounded-lg overflow-hidden">
              <div className="bg-purple-100 p-3 flex flex-col gap-3">
                <div className="flex items-center justify-between">
                  <span className="font-semibold text-purple-800">
                    SMS Consent Records ({consents.length})
                  </span>
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-purple-600">
                      {selectedConsents.length} selected
                    </span>
                    <Button 
                      size="sm" 
                      variant="outline" 
                      className="border-purple-300 text-purple-700 text-xs"
                      onClick={selectAllPendingConsents}
                    >
                      Select Pending
                    </Button>
                    <Button 
                      size="sm" 
                      variant="outline" 
                      className="border-gray-300 text-gray-600 text-xs"
                      onClick={clearConsentSelection}
                    >
                      Clear All
                    </Button>
                    <Button 
                      size="sm"
                      onClick={confirmSendOptInRequests}
                      disabled={sendingOptIn || selectedConsents.length === 0 || (!smsSubscribed)}
                      className={(smsSubscribed) ? "bg-green-600 hover:bg-green-700 text-white text-xs" : "bg-gray-400 text-white text-xs cursor-not-allowed"}
                      title={(!smsSubscribed) ? "SMS subscription required" : ""}
                    >
                      {sendingOptIn ? (
                        <>
                          <RefreshCw className="w-3 h-3 mr-1 animate-spin" />
                          Sending...
                        </>
                      ) : (
                        <>
                          {(!smsSubscribed) && <Lock className="w-3 h-3 mr-1" />}
                          <Send className="w-3 h-3 mr-1" />
                          Send Opt-In Request ({selectedConsents.length})
                        </>
                      )}
                    </Button>
                  </div>
                </div>
                {/* Search Box */}
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-purple-400" />
                  <Input
                    type="text"
                    placeholder="Search by parent name, child name, or phone number..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10 border-purple-200 bg-white focus:ring-purple-400"
                    data-testid="parent-search-input"
                  />
                  {searchTerm && (
                    <button
                      onClick={() => setSearchTerm("")}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      <XCircle className="w-4 h-4" />
                    </button>
                  )}
                </div>
                {searchTerm && (
                  <p className="text-xs text-purple-600">
                    Showing {filteredConsents.length} of {consents.length} records
                  </p>
                )}
              </div>
              
              {loadingConsents ? (
                <div className="p-8 text-center text-gray-500">Loading...</div>
              ) : consents.length === 0 ? (
                <div className="p-8 text-center text-gray-500">
                  <Users className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                  <p>No consent records found.</p>
                  <p className="text-sm">Click "Sync from Children" to import contacts.</p>
                </div>
              ) : filteredConsents.length === 0 ? (
                <div className="p-8 text-center text-gray-500">
                  <Search className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                  <p>No matches found for "{searchTerm}"</p>
                  <p className="text-sm">Try a different search term.</p>
                </div>
              ) : (
                <div className="max-h-96 overflow-y-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-purple-50 sticky top-0">
                      <tr>
                        <th className="p-2 w-10 text-center text-purple-700">
                          <Checkbox
                            checked={selectedConsents.length > 0 && selectedConsents.length === filteredConsents.filter(c => !c.smsOptInReceived).length}
                            onCheckedChange={(checked) => checked ? selectAllPendingConsents() : clearConsentSelection()}
                          />
                        </th>
                        <th className="p-2 text-left text-purple-700">Parent Name</th>
                        <th className="p-2 text-left text-purple-700">Phone</th>
                        <th className="p-2 text-left text-purple-700">Child</th>
                        <th className="p-2 text-center text-purple-700">Form Signed</th>
                        <th className="p-2 text-center text-purple-700">Request Sent</th>
                        <th className="p-2 text-center text-purple-700">SMS Opt-In</th>
                        <th className="p-2 text-center text-purple-700">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredConsents.map((consent) => (
                        <tr key={consent.id} className={`border-t border-purple-100 hover:bg-purple-50 ${selectedConsents.includes(consent.id) ? 'bg-purple-100' : ''}`}>
                          <td className="p-2 text-center">
                            {!consent.smsOptInReceived && (
                              <Checkbox
                                checked={selectedConsents.includes(consent.id)}
                                onCheckedChange={() => toggleConsentSelection(consent.id)}
                              />
                            )}
                          </td>
                          <td className="p-2 font-medium">{consent.parentName}</td>
                          <td className="p-2 text-gray-600">{formatPhoneNumber(consent.phoneNumber)}</td>
                          <td className="p-2 text-gray-600">{consent.childName}</td>
                          <td className="p-2 text-center">
                            <div className="flex flex-col items-center gap-1">
                              <Checkbox
                                checked={consent.enrollmentFormSigned}
                                onCheckedChange={(checked) => 
                                  updateConsent(consent.id, 'enrollmentFormSigned', checked)
                                }
                              />
                              {consent.enrollmentFormSignedDate && (
                                <span className="text-xs text-gray-500">
                                  {format(new Date(consent.enrollmentFormSignedDate), "M/d/yy")}
                                </span>
                              )}
                            </div>
                          </td>
                          <td className="p-2 text-center">
                            {consent.optInRequestSent ? (
                              <div className="flex flex-col items-center">
                                <Badge className="bg-cyan-100 text-cyan-700 text-xs">
                                  <Send className="w-3 h-3 mr-1" />
                                  Sent
                                </Badge>
                                {consent.optInRequestSentAt && (
                                  <span className="text-xs text-gray-500 mt-1">
                                    {format(new Date(consent.optInRequestSentAt), "M/d/yy")}
                                  </span>
                                )}
                              </div>
                            ) : (
                              <Badge variant="outline" className="text-gray-400 text-xs">
                                Not Sent
                              </Badge>
                            )}
                          </td>
                          <td className="p-2 text-center">
                            {consent.smsOptInReceived ? (
                              <div className="flex flex-col items-center">
                                <Badge className="bg-green-100 text-green-700 text-xs">
                                  <CheckCircle className="w-3 h-3 mr-1" />
                                  Opted In
                                </Badge>
                                {consent.smsOptInReceivedAt && (
                                  <span className="text-xs text-gray-500 mt-1">
                                    {format(new Date(consent.smsOptInReceivedAt), "M/d/yy h:mm a")}
                                  </span>
                                )}
                              </div>
                            ) : (
                              <Badge variant="outline" className="text-gray-500 text-xs">
                                <Clock className="w-3 h-3 mr-1" />
                                Pending
                              </Badge>
                            )}
                          </td>
                          <td className="p-2 text-center">
                            <div className="flex flex-col items-center gap-1">
                              {selectedConsents.includes(consent.id) && (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="text-xs border-red-300 text-red-600 hover:bg-red-50"
                                  onClick={() => toggleConsentSelection(consent.id)}
                                >
                                  <XCircle className="w-3 h-3 mr-1" />
                                  Deselect
                                </Button>
                              )}
                              {!consent.smsOptInReceived && !selectedConsents.includes(consent.id) && (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="text-xs border-green-300 text-green-700 hover:bg-green-50"
                                  onClick={() => recordOptIn(consent.id)}
                                >
                                  <CheckCircle className="w-3 h-3 mr-1" />
                                  Manual Opt-In
                                </Button>
                              )}
                              {/* Clear button - always available to reset all consent fields */}
                              {(consent.smsOptInReceived || consent.optInRequestSent || consent.enrollmentFormSigned) && (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="text-xs border-amber-300 text-amber-600 hover:bg-amber-50"
                                  onClick={() => resetOptIn(consent.id)}
                                >
                                  <RefreshCw className="w-3 h-3 mr-1" />
                                  Clear All
                                </Button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </TabsContent>

          {/* ============== MESSAGING TAB ============== */}
          <TabsContent value="messaging" className="space-y-6">

            {!hideBilling && (<>
            {/* ── SUBSCRIPTION SECTION — Always visible at top, mirrors Pickup Safety ── */}
            <Card className="border-2 border-blue-300 bg-gradient-to-br from-blue-50 via-white to-purple-50">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-blue-800">
                  <MessageSquare className="w-5 h-5" />
                  Subscribe to Parent SMS Communications
                </CardTitle>
                <p className="text-sm text-gray-600">Send instant text messages to parents for emergencies, closures, pickups & daily updates</p>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Plan Selection */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className={`p-4 rounded-xl border-2 cursor-pointer transition-all relative ${selectedPlan === 'monthly' ? 'border-blue-500 bg-blue-50 shadow-md' : 'border-gray-200 hover:border-blue-300'}`} onClick={() => setSelectedPlan('monthly')}>
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-semibold text-gray-900">Monthly</span>
                      <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${selectedPlan === 'monthly' ? 'border-blue-500 bg-blue-500' : 'border-gray-300'}`}>
                        {selectedPlan === 'monthly' && <Check className="w-3 h-3 text-white" />}
                      </div>
                    </div>
                    <p className="text-3xl font-bold text-gray-900">$24.99<span className="text-base font-normal text-gray-500">/month</span></p>
                  </div>
                  <div className={`p-4 rounded-xl border-2 cursor-pointer transition-all relative ${selectedPlan === 'annual' ? 'border-green-500 bg-green-50 shadow-md' : 'border-gray-200 hover:border-green-300'}`} onClick={() => setSelectedPlan('annual')}>
                    <Badge className="absolute -top-2 -right-2 bg-green-500 text-white">Best Value!</Badge>
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-semibold text-gray-900">Annual</span>
                      <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${selectedPlan === 'annual' ? 'border-green-500 bg-green-500' : 'border-gray-300'}`}>
                        {selectedPlan === 'annual' && <Check className="w-3 h-3 text-white" />}
                      </div>
                    </div>
                    <p className="text-3xl font-bold text-gray-900"><span className="line-through text-gray-400 text-xl mr-2">$299.88</span>$269.89<span className="text-base font-normal text-gray-500">/year</span></p>
                    <p className="text-sm text-green-600 font-medium mt-1">Save $29.99 — 10% off at checkout</p>
                  </div>
                </div>

                {/* Email warning */}
                {user?.email && (
                  <div className="rounded-xl border border-blue-300 bg-blue-50 p-4 space-y-2">
                    <p className="flex items-center gap-2 text-sm font-bold text-blue-800"><AlertTriangleIcon className="h-4 w-4" /> Important — use this email at Square checkout</p>
                    <p className="text-sm text-blue-700">When prompted for an email on Square, you <strong>MUST</strong> enter:</p>
                    <p className="rounded-lg bg-white px-3 py-2 text-sm font-bold text-blue-900 ring-1 ring-blue-300">{user.email}</p>
                    <p className="text-xs text-blue-600">This links your subscription to your account. A different email will delay activation.</p>
                  </div>
                )}

                {/* Subscribe Button */}
                <Button onClick={handleSubscribe} disabled={subscribing} className="w-full bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white py-6 text-lg font-semibold rounded-xl shadow-lg">
                  {subscribing ? <><Loader2 className="w-5 h-5 mr-2 animate-spin" />Processing...</> : <><CreditCard className="w-5 h-5 mr-2" />Subscribe Now — {selectedPlan === 'monthly' ? '$24.99/month' : '$269.89/year'}</>}
                </Button>
                <p className="text-center text-sm text-gray-500">After paying on Square, return here and click the <strong>refresh button at the top of the page</strong> to activate.</p>

                {/* Active state */}
                {smsSubscribed && (
                  <div className="bg-green-50 border border-green-200 rounded-xl p-4 flex items-center justify-between gap-3">
                    <div className="flex items-center gap-3">
                      <CheckCircle2 className="w-6 h-6 text-green-600 flex-shrink-0" />
                      <p className="font-semibold text-green-800">Parent Communication SMS Active</p>
                    </div>
                    <Button onClick={handleCancelSubscription} disabled={canceling} variant="outline" className="border-red-200 text-red-600 hover:bg-red-50 flex-shrink-0">
                      {canceling ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Canceling...</> : 'Cancel at anytime'}
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* ── PARENT BILLING SETUP — mirrors Pickup Safety ── */}
            <Card className="border-2 border-emerald-400 shadow-lg">
              <CardHeader className="bg-gradient-to-r from-emerald-600 to-teal-700 rounded-t-xl">
                <CardTitle className="flex items-center gap-2 text-white">
                  <DollarSign className="w-5 h-5" />
                  Parent Billing Setup
                </CardTitle>
                <p className="text-sm text-emerald-100">Set up fees to charge parents for SMS communications after the center subscription is active</p>
              </CardHeader>
              <CardContent className="pt-6 space-y-6">
                {/* Enable toggle */}
                <div className={`flex items-center justify-between p-4 rounded-lg border ${smsSubscribed || isPlatformOwner ? 'bg-blue-50 border-blue-200' : 'bg-slate-100 border-slate-200'}`}>
                  <div>
                    <p className={`font-medium ${smsSubscribed || isPlatformOwner ? 'text-gray-900' : 'text-slate-500'}`}>Charge Parents for SMS Communications</p>
                    <p className="text-sm text-gray-500">
                      {smsSubscribed || isPlatformOwner
                        ? 'Enable to set up parent billing for SMS messaging'
                        : 'Complete the center subscription first, then parent billing becomes available'}
                    </p>
                  </div>
                  {smsSubscribed || isPlatformOwner ? (
                    <Switch checked={smsBilling.chargeParents} onCheckedChange={v => handleBillingChange('chargeParents', v)} />
                  ) : (
                    <div className="flex items-center gap-2 text-slate-400"><Lock className="h-5 w-5" /><span className="text-sm font-medium">Locked</span></div>
                  )}
                </div>

                {smsBilling.chargeParents && (smsSubscribed || isPlatformOwner) && (
                  <>
                    {/* SMS Fee */}
                    <div className="p-4 bg-white rounded-lg border border-gray-200">
                      <div className="flex items-center gap-2 mb-3">
                        <MessageSquare className="w-5 h-5 text-blue-600" />
                        <label className="font-medium text-gray-800">SMS Fee Per Parent/Child</label>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-gray-500">$</span>
                        <input type="number" min="0" step="0.50"
                          value={smsBilling.smsPerChildFee === 0 ? '' : smsBilling.smsPerChildFee}
                          onChange={e => handleBillingChange('smsPerChildFee', e.target.value === '' ? 0 : parseFloat(e.target.value))}
                          className="w-24 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                          placeholder="0.00" />
                        <span className="text-gray-500">/ child / month</span>
                      </div>
                      <p className="text-xs text-gray-400 mt-2">Set to $0 for free.</p>
                    </div>

                    {/* Billing Frequency */}
                    <div className="p-4 bg-white rounded-lg border border-gray-200">
                      <label className="font-medium text-gray-800 block mb-3">Billing Frequency</label>
                      <div className="flex gap-3">
                        {['monthly', 'annually'].map(freq => (
                          <button key={freq} type="button" onClick={() => handleBillingChange('billingFrequency', freq)}
                            className={`px-4 py-2 rounded-lg border-2 transition-all capitalize ${smsBilling.billingFrequency === freq ? 'border-blue-500 bg-blue-50 text-blue-700 font-medium' : 'border-gray-200 text-gray-600 hover:border-blue-300'}`}>
                            {freq}
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Billing Day + Mode */}
                    <div className="grid grid-cols-2 gap-4">
                      <div className="p-4 bg-white rounded-lg border border-gray-200">
                        <label className="font-medium text-gray-800 block mb-3">Billing Day of Month</label>
                        <select value={smsBilling.billingDay || 1} onChange={e => setSmsBilling(p => ({...p, billingDay: parseInt(e.target.value)}))}
                          className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500">
                          {Array.from({length:28},(_,i)=>i+1).map(d => <option key={d} value={d}>{d === 1 ? '1st' : d === 2 ? '2nd' : d === 3 ? '3rd' : `${d}th`} of the month</option>)}
                        </select>
                        <p className="text-xs text-gray-400 mt-1">Director gets reminder day before</p>
                      </div>
                      <div className="p-4 bg-white rounded-lg border border-gray-200">
                        <label className="font-medium text-gray-800 block mb-3">Send Mode</label>
                        <div className="space-y-2">
                          {[{val:'auto',label:'Auto-Send',desc:'Sends automatically on billing day'},{val:'manual',label:'Manual Approval',desc:'Queue until you approve'}].map(m => (
                            <label key={m.val} className={`flex items-center gap-2 p-2 rounded-lg cursor-pointer border ${smsBilling.billingMode === m.val ? 'border-blue-400 bg-blue-50' : 'border-gray-200'}`}>
                              <input type="radio" checked={smsBilling.billingMode === m.val} onChange={() => setSmsBilling(p => ({...p, billingMode: m.val}))} className="accent-blue-600" />
                              <div><p className="text-xs font-semibold text-gray-800">{m.label}</p><p className="text-xs text-gray-400">{m.desc}</p></div>
                            </label>
                          ))}
                        </div>
                      </div>
                    </div>

                    {/* Fee Passthrough Toggle */}
                    {(() => {
                      const base = parseFloat(smsBilling.smsPerChildFee) || 0;
                      const squareFee = +(base * 0.029 + 0.30).toFixed(2);
                      const platformFee = 5.00;
                      const parentPays = smsBilling.feePassthrough ? +(base + squareFee + platformFee).toFixed(2) : base;
                      const centerNets = smsBilling.feePassthrough ? base : +(base - squareFee - platformFee).toFixed(2);
                      return (
                        <div className="p-4 bg-white rounded-lg border-2 border-blue-200 space-y-3">
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="font-semibold text-gray-800">Pass Fees to Parent</p>
                              <p className="text-xs text-gray-500">Square processing fee + $5 platform fee</p>
                            </div>
                            <button type="button"
                              onClick={() => setSmsBilling(p => ({...p, feePassthrough: !p.feePassthrough}))}
                              className={`relative inline-flex h-7 w-12 items-center rounded-full transition-colors ${smsBilling.feePassthrough ? 'bg-blue-600' : 'bg-gray-300'}`}>
                              <span className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${smsBilling.feePassthrough ? 'translate-x-6' : 'translate-x-1'}`} />
                            </button>
                          </div>
                          <div className={`rounded-lg p-3 text-xs space-y-1 ${smsBilling.feePassthrough ? 'bg-blue-50 border border-blue-200' : 'bg-orange-50 border border-orange-200'}`}>
                            <p className="font-bold text-gray-700 mb-2">{smsBilling.feePassthrough ? '✓ Pass fees to parent' : '✓ Center absorbs fees'} — Example at ${base.toFixed(2)}/child:</p>
                            <div className="space-y-1 font-mono text-gray-600">
                              <div className="flex justify-between"><span>Your SMS fee</span><span>${base.toFixed(2)}</span></div>
                              <div className={`flex justify-between ${smsBilling.feePassthrough ? 'text-orange-600' : 'text-gray-400 line-through'}`}><span>Square processing (2.9%+$0.30)</span><span>+${squareFee.toFixed(2)}</span></div>
                              <div className={`flex justify-between ${smsBilling.feePassthrough ? 'text-orange-600' : 'text-gray-400 line-through'}`}><span>Platform fee</span><span>+$5.00</span></div>
                              <div className="border-t pt-1 mt-1 flex justify-between font-bold text-gray-800"><span>Parent pays</span><span>${parentPays.toFixed(2)}</span></div>
                              <div className={`flex justify-between font-bold ${centerNets < 0 ? 'text-red-600' : 'text-green-700'}`}><span>You receive</span><span>${centerNets.toFixed(2)}</span></div>
                            </div>
                          </div>
                        </div>
                      );
                    })()}

                    {/* Revenue Calculator — based on "you receive" amount */}
                    {(() => {
                      const base = parseFloat(smsBilling.smsPerChildFee) || 0;
                      const squareFee = +(base * 0.029 + 0.30).toFixed(2);
                      const youReceive = smsBilling.feePassthrough ? base : +(base - squareFee - 5).toFixed(2);
                      const n = parseInt(smsBilling.estimateChildren) || childCount || 0;
                      return (
                        <div className="rounded-xl border border-green-200 bg-green-50/40 p-3 space-y-2">
                          <div className="flex items-center justify-between gap-3">
                            <p className="text-sm font-semibold text-green-800">Estimated Revenue</p>
                            <div className="flex items-center gap-1.5">
                              <label className="text-xs text-green-700">Children:</label>
                              <input type="number" min="1" value={smsBilling.estimateChildren}
                                onChange={e => setSmsBilling(p => ({...p, estimateChildren: e.target.value}))}
                                placeholder={String(childCount || 0)}
                                className="w-16 h-7 border border-green-300 rounded-lg px-2 text-xs bg-white focus:ring-1 focus:ring-green-400 text-center" />
                            </div>
                          </div>
                          <div className="grid grid-cols-2 gap-2">
                            <div className="bg-white rounded-lg p-2 border border-green-200 text-center">
                              <p className="text-lg font-bold text-green-700">${(n * youReceive).toFixed(2)}</p>
                              <p className="text-xs text-green-600">Monthly (you receive)</p>
                            </div>
                            <div className="bg-white rounded-lg p-2 border border-green-200 text-center">
                              <p className="text-lg font-bold text-green-700">${(n * youReceive * 12).toFixed(2)}</p>
                              <p className="text-xs text-green-600">Yearly (you receive)</p>
                            </div>
                          </div>
                          {youReceive < 0 && <p className="text-xs text-red-600 font-medium">⚠️ Your fee is too low to cover costs — increase fee or enable fee passthrough.</p>}
                        </div>
                      );
                    })()}

                    {/* Payment Setup — Zelle + Square + fee disclosure */}
                    <div className="space-y-3">
                      <h4 className="font-semibold text-gray-800">How Parent Payments Work</h4>
                      {/* Zelle */}
                      <div className="p-4 bg-white rounded-lg border border-gray-200 space-y-2">
                        <p className="font-medium text-gray-700">Zelle Payment Option</p>
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <label className="text-xs text-gray-500">Zelle Email</label>
                            <input type="email" value={smsBilling.zelleEmail} onChange={e => handleBillingChange('zelleEmail', e.target.value)}
                              placeholder="your@email.com" className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500" />
                          </div>
                          <div>
                            <label className="text-xs text-gray-500">Zelle Phone</label>
                            <input type="tel" value={smsBilling.zellePhone} onChange={e => handleBillingChange('zellePhone', e.target.value)}
                              placeholder="(555) 000-0000" className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500" />
                          </div>
                        </div>
                      </div>
                      {/* Square */}
                      <div className="p-4 bg-white rounded-lg border border-gray-200 space-y-2">
                        <div className="flex items-center justify-between">
                          <p className="font-medium text-gray-700">Credit Card via Square</p>
                          <Switch checked={smsBilling.parentSquareEnabled} onCheckedChange={v => handleBillingChange('parentSquareEnabled', v)} />
                        </div>

                        {smsBilling.parentSquareEnabled && (
                          <SquareSetupGuide
                            squareConnected={smsBilling.squareConnected}
                            onConnect={async () => {
                              try {
                                const res = await axios.get(`${API}/square/authorize`);
                                window.location.href = res.data.authorization_url;
                              } catch { toast.error("Unable to start Square connection"); }
                            }}
                          />
                        )}
                      </div>
                    </div>

                    {/* Save button */}
                    <Button onClick={saveSmsBilling} disabled={savingBilling} className="w-full bg-blue-600 hover:bg-blue-700 text-white">
                      {savingBilling ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Saving...</> : <><Save className="w-4 h-4 mr-2" />Save Billing Settings</>}
                    </Button>
                  </>
                )}
              </CardContent>
            </Card>

            {/* ── SEND INVOICES + PAYMENT TRACKER ── */}
            {(smsSubscribed || isPlatformOwner) && smsBilling.chargeParents && (
              <SmsBillingPanel
                smsBilling={smsBilling}
                childCount={childCount}
                orgId={organization?.id}
              />
            )}
            {!smsSubscribed && !isPlatformOwner && messageType === 'sms' && (
              <div className="rounded-2xl overflow-hidden border-2 border-orange-300 shadow-lg">
                <div className="bg-gradient-to-r from-orange-500 via-rose-500 to-pink-500 px-6 py-5 text-white">
                  <div className="flex items-center justify-between flex-wrap gap-4">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-white/20 rounded-xl">
                        <Phone className="w-6 h-6" />
                      </div>
                      <div>
                        <h3 className="text-lg font-bold">Parent Communication SMS — Unlock SMS Messaging</h3>
                        <p className="text-white/90 text-sm mt-0.5">Send instant text alerts to parents for emergencies, closures, pickups & daily updates</p>
                      </div>
                    </div>
                    <div className="flex flex-col items-end gap-2">
                      <div className="text-right">
                        <span className="text-3xl font-black">$24.99</span>
                        <span className="text-white/80 text-sm">/month</span>
                        <p className="text-white/70 text-xs">or $269.89/year</p>
                      </div>
                      <button
                        onClick={() => window.location.href = '/settings/parent-communication'}
                        className="bg-white text-orange-600 font-black text-sm px-6 py-2.5 rounded-xl shadow-lg hover:bg-orange-50 transition-all hover:scale-105"
                      >
                        Subscribe Now →
                      </button>
                    </div>
                  </div>
                  <div className="mt-4 pt-4 border-t border-white/20 grid grid-cols-2 md:grid-cols-4 gap-2">
                    {['Emergency & weather alerts','Pickup safety notifications','Mass parent texting','SMS consent tracking'].map(f => (
                      <div key={f} className="flex items-center gap-1.5 text-xs text-white/90">
                        <CheckCircle className="w-3.5 h-3.5 text-green-300 flex-shrink-0" />
                        {f}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
            </>)}

            {/* Full messaging interface — always visible, dimmed when not subscribed on SMS */}
            <div className={(!smsSubscribed && !isPlatformOwner && messageType === 'sms') ? 'opacity-40 pointer-events-none select-none' : ''}>
            {/* Audience selector */}
            <div className="mb-3">
              <Label className="text-purple-700">Audience</Label>
              <Select
                value={audience}
                onValueChange={(v) => { setAudience(v); setSelectedParents([]); }}
              >
                <SelectTrigger className="border-purple-200" data-testid="audience-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="parents">Parents only</SelectItem>
                  <SelectItem value="staff">Staff only</SelectItem>
                  <SelectItem value="both">Parents + Staff</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Filter and Select */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label className="text-purple-700">Filter by Category {audience === 'staff' && <span className="text-xs text-gray-400">(parents only)</span>}</Label>
                <Select value={parentFilterCategory} onValueChange={setParentFilterCategory} disabled={audience === 'staff'}>
                  <SelectTrigger className="border-purple-200">
                    <SelectValue placeholder="All Categories" />
                  </SelectTrigger>
                  <SelectContent>
                    {CATEGORIES.map(cat => (
                      <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-purple-700">Message Type</Label>
                <Select value={messageType} onValueChange={setMessageType}>
                  <SelectTrigger className="border-purple-200">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="sms">
                      <div className="flex items-center gap-2">
                        <Phone className="w-4 h-4" />
                        SMS Text Message
                        {!smsSubscribed && <Lock className="w-3 h-3 text-gray-400" />}
                      </div>
                    </SelectItem>
                    {ENABLE_EMAIL_CHANNEL && (
                      <SelectItem value="email">
                        <div className="flex items-center gap-2">
                          <Mail className="w-4 h-4" />
                          Email
                        </div>
                      </SelectItem>
                    )}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Parent Selection List */}
            <div className="border-2 border-purple-100 rounded-lg p-4">
              <div className="flex items-center justify-between mb-3">
                <Label className="text-purple-800 font-semibold">
                  Select Recipients ({selectedParents.length} selected)
                </Label>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" className="border-purple-300 text-purple-700" onClick={handleSelectAll}>
                    <CheckSquare className="w-4 h-4 mr-1" />
                    Select All
                  </Button>
                  <Button variant="outline" size="sm" className="border-gray-300 text-gray-700" onClick={handleClearSelection}>
                    <Square className="w-4 h-4 mr-1" />
                    Clear
                  </Button>
                </div>
              </div>
              
              <div className="max-h-64 overflow-y-auto space-y-2">
                {filteredContacts.map((contact, idx) => {
                  const isSelected = selectedParents.some(p => p.dedupKey === contact.dedupKey);
                  const childList = (contact.childNames || []).join(', ');
                  return (
                    <div
                      key={`${contact.dedupKey}-${idx}`}
                      onClick={() => toggleContact(contact)}
                      data-testid={`recipient-row-${contact.dedupKey}`}
                      className={`p-3 rounded-lg cursor-pointer transition-colors ${
                        isSelected 
                          ? 'bg-purple-100 border-2 border-purple-400' 
                          : 'bg-gray-50 border-2 border-transparent hover:bg-purple-50'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-medium text-gray-800 flex items-center gap-2">
                            {contact.parentName}
                            {contact.contactType === 'staff' && (
                              <span className="text-[10px] font-bold tracking-wide bg-indigo-100 text-indigo-700 border border-indigo-200 px-1.5 py-0.5 rounded">STAFF</span>
                            )}
                          </p>
                          {contact.contactType !== 'staff' && (
                            <p className="text-sm text-gray-600">
                              {contact.childNames && contact.childNames.length > 1
                                ? `Children: ${childList}`
                                : `Child: ${childList} (${contact.childCategory?.replace('_', ' ')})`}
                            </p>
                          )}
                          <p className="text-xs text-gray-500">
                            {messageType === 'sms' ? (
                              <span className="flex items-center gap-1">
                                <Phone className="w-3 h-3" /> {formatPhoneNumber(contact.phone)}
                              </span>
                            ) : (
                              <span className="flex items-center gap-1">
                                <Mail className="w-3 h-3" /> {contact.email}
                              </span>
                            )}
                          </p>
                        </div>
                        {isSelected 
                          ? <CheckSquare className="w-5 h-5 text-purple-600 flex-shrink-0" />
                          : <Square className="w-5 h-5 text-gray-300 flex-shrink-0" />
                        }
                      </div>
                    </div>
                  );
                })}
                {filteredContacts.length === 0 && (
                  <p className="text-center text-gray-500 py-4">
                    No parents found with {messageType === 'sms' ? 'phone numbers' : 'email addresses'}
                  </p>
                )}
              </div>
            </div>

            {/* Message Composition */}
            <div className="space-y-4">
              {messageType === 'email' && (
                <div>
                  <Label htmlFor="msg-subject" className="text-purple-700">Subject</Label>
                  <Input
                    id="msg-subject"
                    value={messageSubject}
                    onChange={(e) => setMessageSubject(e.target.value)}
                    placeholder="Enter email subject..."
                    className="border-purple-200"
                  />
                </div>
              )}
              {messageType === 'sms' && (
                <div>
                  <Label htmlFor="msg-sender-name" className="text-purple-700">Your Name <span className="text-red-500">*</span></Label>
                  <Input
                    id="msg-sender-name"
                    value={senderName}
                    onChange={(e) => setSenderName(e.target.value)}
                    placeholder="Your name is required so parents know who is texting them — e.g., Mrs. Stacey"
                    className="border-purple-200 placeholder:text-gray-400"
                    data-testid="sms-sender-name-input"
                  />
                </div>
              )}
              <div>
                <Label className="text-purple-700">Message</Label>
                <Textarea
                  value={messageBody}
                  onChange={(e) => setMessageBody(e.target.value)}
                  placeholder={`Enter your ${messageType === 'sms' ? 'text' : 'email'} message...`}
                  className="border-purple-200 min-h-[120px]"
                />
                {messageType === 'sms' && (
                  <p className="text-xs text-gray-500 mt-1">
                    Character count: {messageBody.length} / 160 (SMS segment)
                  </p>
                )}
              </div>

              {/* SMS Compliance Message - Required by Twilio */}
              {messageType === 'sms' && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                  <div className="flex items-start gap-2">
                    <Checkbox
                      id="include-consent"
                      checked={includeConsent}
                      onCheckedChange={setIncludeConsent}
                      className="mt-0.5"
                    />
                    <div className="flex-1">
                      <Label htmlFor="include-consent" className="text-sm font-medium text-amber-800 cursor-pointer">
                        Include opt-out message (Required by Twilio for A2P compliance)
                      </Label>
                      <p className="text-xs text-amber-700 mt-1 italic">
                        The following will be appended to your message:
                      </p>
                      <p className="text-xs text-amber-900 mt-1 bg-amber-100 p-2 rounded font-mono">
                        "Reply STOP to opt out of future messages."
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Action Buttons */}
            <div className="flex justify-between">
              <Button 
                variant="outline" 
                className="border-purple-300 text-purple-700"
                onClick={() => setShowMessageHistory(!showMessageHistory)}
              >
                <History className="w-4 h-4 mr-2" />
                Message History ({messageHistory.length})
              </Button>
              <Button
                onClick={confirmSendMessage}
                disabled={sendingMessage || selectedParents.length === 0 || !messageBody.trim() || (messageType === 'sms' && !senderName.trim())}
                className="bg-purple-600 hover:bg-purple-700 text-white"
              >
                {sendingMessage ? (
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    Sending...
                  </div>
                ) : (
                  <>
                    <Send className="w-4 h-4 mr-2" />
                    Send {messageType === 'sms' ? 'SMS' : 'Email'} ({selectedParents.length})
                  </>
                )}
              </Button>
            </div>

            {/* Message History Panel */}
            {showMessageHistory && (
              <div className="border-2 border-purple-100 rounded-lg p-4 bg-purple-50">
                <h3 className="text-lg font-semibold text-purple-800 mb-4">Message History</h3>
                {messageHistory.length === 0 ? (
                  <p className="text-gray-500 italic">No messages sent yet.</p>
                ) : (
                  <div className="space-y-3 max-h-64 overflow-y-auto">
                    {messageHistory.slice(0, 20).map((msg) => (
                      <div key={msg.id} className="bg-white p-3 rounded-lg border border-purple-200">
                        <div className="flex items-center justify-between mb-2">
                          <span className={`text-xs px-2 py-1 rounded ${
                            msg.messageType === 'sms' ? 'bg-blue-100 text-blue-700' : 'bg-green-100 text-green-700'
                          }`}>
                            {msg.messageType?.toUpperCase()}
                          </span>
                          <span className="text-xs text-gray-500">
                            {msg.sentAt ? format(new Date(msg.sentAt), 'MMM d, yyyy h:mm a') : 'Unknown'}
                          </span>
                        </div>
                        <p className="text-sm text-gray-700 line-clamp-2">{msg.message}</p>
                        <p className="text-xs text-gray-500 mt-1">
                          Sent to {msg.sentCount || 0} recipients by {msg.sentByName || 'Unknown'}
                          {msg.messageType === 'sms' && msg.senderName && (
                            <span className="ml-1 font-medium text-blue-600">· SMS by {msg.senderName}</span>
                          )}
                        </p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
            </div>{/* end dimmed wrapper */}
          </TabsContent>
        </Tabs>
          </div>{/* end content wrapper */}

          {/* Fade overlay — non-owners only when not subscribed */}
          {!smsSubscribed && (
            <div className="absolute inset-0 bg-gradient-to-b from-transparent via-white/50 to-white flex items-end justify-center pb-12 z-10">
              <div className="flex flex-col items-center gap-4 text-center">
                <div className="bg-orange-500 rounded-full p-4 shadow-lg">
                  <Lock className="w-8 h-8 text-white" />
                </div>
                <Button
                  className="bg-orange-500 hover:bg-orange-600 text-white px-8 py-3 text-lg shadow-lg"
                  onClick={() => hideBilling ? navigate('/parent-communication?tab=billing') : (window.location.href = '/settings/parent-communication')}
                >
                  <Phone className="w-5 h-5 mr-2" />
                  Subscribe — $24.99/month
                </Button>
              </div>
            </div>
          )}
        </div>{/* end relative wrapper */}
      </CardContent>

      {/* Confirmation Dialog for Opt-In Request */}
      <AlertDialog open={showOptInConfirm} onOpenChange={setShowOptInConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirm Send Opt-In Requests</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to send opt-in requests to <strong>{selectedConsents.length}</strong> parent(s)?
              <br /><br />
              This will send an SMS to each selected parent asking them to reply "YES" to consent to receiving text messages.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={sendOptInRequests} className="bg-green-600 hover:bg-green-700">
              Yes, Send Requests
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Confirmation Dialog for Send Message */}
      <AlertDialog open={showMessageConfirm} onOpenChange={setShowMessageConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirm Send Message</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div>
                <p>Are you sure you want to send this {messageType === 'sms' ? 'SMS' : 'Email'} to <strong>{selectedParents.length}</strong> {audience === 'staff' ? 'staff member(s)' : audience === 'both' ? 'recipient(s)' : 'parent(s)'}?</p>
                <p className="mt-3 font-medium">Message Preview:</p>
                <div className="mt-2 p-3 bg-gray-100 rounded-lg text-sm max-h-40 overflow-y-auto">
                  {messageSubject && <div className="font-medium mb-1">Subject: {messageSubject}</div>}
                  <p>{messageBody}</p>
                  {messageType === 'sms' && includeConsent && (
                    <p className="mt-2 pt-2 border-t border-gray-300 text-amber-700 italic text-xs">
                      Reply STOP to opt out of future messages.
                    </p>
                  )}
                </div>
                {messageType === 'sms' && includeConsent && (
                  <p className="text-xs text-amber-600 mt-2">
                    * Opt-out message will be included (Twilio A2P compliance)
                  </p>
                )}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleSendMessage} className="bg-purple-600 hover:bg-purple-700">
              Yes, Send Message
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
    </FeatureGate>
  );
};

export default ParentCommunicationTab;
